import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { HumisLogo } from '@/components/brand/HumisLogo'
import { useAuthStore } from '@/stores/authStore'
import { toApiProblem } from '@/lib/api/client'
import { problemMessage } from '@/lib/api/problem'

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginValues = z.infer<typeof loginSchema>

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const login = useAuthStore((state) => state.login)

  const [serverError, setServerError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) })

  async function onSubmit(values: LoginValues) {
    setServerError(null)
    try {
      await login(values.email, values.password)
      // Return the user to whatever they were trying to reach before the guard bounced them.
      const from = (location.state as { from?: string } | null)?.from ?? '/'
      void navigate(from, { replace: true })
    } catch (error) {
      const problem = toApiProblem(error)

      // The backend returns RFC 7807 with a flat `errors` array; map it onto the fields.
      if (problem?.errors?.length) {
        for (const fieldError of problem.errors) {
          setError(fieldError.field as keyof LoginValues, { message: fieldError.message })
        }
        return
      }
      setServerError(problemMessage(problem ?? error, t('auth.invalidCredentials')))
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-[var(--color-page)] p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          {/* The lockup carries the name and tagline as artwork; the h1 keeps them for assistive tech. */}
          <HumisLogo variant="full" alt="" className="h-16 w-auto max-w-full" />
          <h1 className="sr-only">
            {t('app.name')} — {t('app.tagline')}
          </h1>
        </div>

        <div className="hrms-card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-heading)]">{t('auth.signIn')}</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">{t('auth.signInSubtitle')}</p>

          <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="mt-5 space-y-4" noValidate>
            {serverError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-red)] px-3 py-2 text-sm text-[var(--color-danger)]"
              >
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>{serverError}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" required>
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                placeholder="admin@smarthrms.local"
                invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p id="email-error" className="text-xs text-[var(--color-danger)]">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" required>
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  className="pe-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute end-0 top-1/2 me-1 grid size-8 -translate-y-1/2 place-items-center rounded text-[var(--color-muted)] hover:text-[var(--color-body)]"
                >
                  {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="text-xs text-[var(--color-danger)]">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
                <input type="checkbox" className="accent-[var(--color-primary)]" />
                {t('auth.rememberMe')}
              </label>
              <Link to="/forgot-password" className="text-sm text-[var(--color-primary)] hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
